import HighchartsReact from 'highcharts-react-official';
import { ReactElement, forwardRef, useRef } from 'react';
import { WidgetType } from 'types/widget.types';
import { ChartSettingsLabelPieDonut } from 'widgets/ChartWidgets/DataTableChartCommon/ChartSettingsLabel';
import { DataTableChartMenuSettings } from 'widgets/ChartWidgets/DataTableChartCommon/DataTableChartCommonMenuSettings';
import { ReadOnlyRenderType } from 'widgets/Widget.types';
import {
  ReadOnlyWidgetBase,
  TransparentImg,
  WidgetBase,
  WidgetBaseProp,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';
import { InteractiveElementWrapper } from 'widgets/sdk/InteractiveElements';

import { useResponsiveHighcharts } from '../ChartWidget.hooks';
import { ChartWidgetToolbar } from '../ChartWidgetToolbar';
import { HighChartsWrapper } from '../HighChartsWrapper';
import { HalfPieChartWidgetData } from './HalfPieChartWidget.types';

export const HalfPieChartWidget = ({ getWidgetMemberComponent: getWidget }: WidgetBaseProp): ReactElement => {
  const { widthPx, heightPx } = useWidget();
  const { openWidgetSideMenu } = useEditor();

  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { customOnResize } = useResponsiveHighcharts({ chartRef, heightPx, widthPx });

  return (
    <WidgetBase onResize={customOnResize} onDoubleClick={openWidgetSideMenu}>
      <WidgetToolbar>
        <ChartWidgetToolbar />
      </WidgetToolbar>
      <WidgetSideMenuSettings>
        <DataTableChartMenuSettings
          dataTableProps={{ colHeaders: true }}
          showBorderColor
          showBorderWidth
          showDataLabels
          showDonutHole
          showLabelsConfiguration
          showLegendsConfiguration
          showPatterns
          showNumberFormatSwitch
          getWidget={getWidget}
          customLabelConfiguration={<ChartSettingsLabelPieDonut widgetType={WidgetType.HalfPieChart} />}
        />
      </WidgetSideMenuSettings>
      <HalfPieChart ref={chartRef} />
    </WidgetBase>
  );
};

interface HalfPieChartProps {
  waitForFonts?: boolean;
}

export const ReadOnlyHalfPieChartWidget = ({ includeAltTextImg }: { includeAltTextImg?: boolean }): ReactElement => {
  const { renderType, interactions } = useWidget<HalfPieChartWidgetData>();
  const waitForFonts = renderType !== ReadOnlyRenderType.ThumbnailReadOnly;
  const isPreview = renderType === ReadOnlyRenderType.StructureTreeList;

  return (
    <ReadOnlyWidgetBase disableSingleSelect>
      <InteractiveElementWrapper interactions={isPreview ? interactions : undefined}>
        <HalfPieChart waitForFonts={waitForFonts} />
      </InteractiveElementWrapper>
      {includeAltTextImg && <TransparentImg />}
    </ReadOnlyWidgetBase>
  );
};

const HalfPieChart = forwardRef<HighchartsReact.RefObject, HalfPieChartProps>(({ waitForFonts = true }, chartRef) => {
  return <HighChartsWrapper chartRef={chartRef} chartType={WidgetType.HalfPieChart} waitForFonts={waitForFonts} />;
});
HalfPieChart.displayName = 'HalfPieChart';
